import axios from '@/lib/axios'
import { useUserSearchStore } from '@/stores/userSearch'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { objectPick, useDebounceFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { MaybeRef, isRef, onBeforeMount, watch } from 'vue'

import { User } from '../interfaces/user'

interface Props {
	params?: Params
	immediate?: boolean
	resetCache?: boolean
	automaticSearch?: boolean
	userInternal?: boolean
}

interface Params {
	q?: string
	filters?: MaybeRef<{
		[key: string]: string | number | boolean | undefined | string[]
	}>
}

const getUsers = async (
	params: Params,
	userInternal: boolean
): Promise<User[]> => {
	const { filters } = params

	const { data } = await axios.get<User[]>(
		`${userInternal ? '/api/internal/users/search' : '/api/users/search'}`,
		{
			params: {
				...objectPick(params, ['q']),
				...(isRef(filters) ? filters.value : filters),
			},
		}
	)

	return data
}

const useUsersSearch = (props?: Props) => {
	const {
		immediate = false,
		resetCache = true,
		automaticSearch = false,
		params = {},
		userInternal = false,
	} = props || {}

	const queryClient = useQueryClient()
	const { setQuery, setUsers, resetState } = useUserSearchStore()
	const { query, users } = storeToRefs(useUserSearchStore())

	const reset = () => {
		queryClient.getQueryCache().find(['userSearch'])?.reset()
		resetState()
	}

	onBeforeMount(() => {
		if (resetCache) {
			reset()
			if (immediate) {
				refetch()
			}
		}
	})

	const { data, isLoading, refetch, isError, error, isFetching, isFetched } =
		useQuery(
			['userSearch'],
			() => getUsers({ ...params, q: query.value || undefined }, userInternal),
			{
				enabled: immediate,
				refetchOnWindowFocus: false,
				retry: false,
			}
		)

	const searchUsers = useDebounceFn(async () => {
		await refetch()
	}, 500)

	watch(query, () => {
		if (automaticSearch) {
			searchUsers()
		}
	})

	watch(
		data,
		(value) => {
			if (value) {
				setUsers(value)
			}
		},
		{
			immediate,
		}
	)

	return {
		query,
		users,
		data,
		isLoading,
		isError,
		error,
		isFetching,
		isFetched,
		refetch,
		resetState,
		searchUsers,
		setQuery,
		setUsers,
		params,
	}
}

export default useUsersSearch
